import { useEffect, useMemo, useState } from "react";
import { api, pdfUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Search, FileText, ArrowUpDown } from "lucide-react";
import { Link } from "react-router-dom";

export default function Reports() {
  const [items, setItems] = useState([]);
  const [patientsMap, setPatientsMap] = useState({});
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [doctor, setDoctor] = useState("");
  const [sortKey, setSortKey] = useState("date_desc");

  useEffect(() => {
    Promise.all([api.get("/consultations"), api.get("/patients")]).then(([c, p]) => {
      setItems(c.data);
      const m = {}; p.data.forEach((x) => m[x.id] = x); setPatientsMap(m);
    });
  }, []);

  const doctors = useMemo(() => Array.from(new Set(items.map((i) => i.doctor_name).filter(Boolean))), [items]);

  const filtered = useMemo(() => {
    let list = items.filter((c) => {
      const date = c.created_at?.slice(0, 10) || "";
      if (from && date < from) return false;
      if (to && date > to) return false;
      if (doctor && c.doctor_name !== doctor) return false;
      if (q) {
        const p = patientsMap[c.patient_id] || {};
        const s = q.toLowerCase();
        const blob = `${c.primary_diagnosis || ""} ${c.diagnosis_text || ""} ${c.chief_complaint || ""} ${c.doctor_name || ""} ${p.pet_name || ""} ${p.owner_name || ""} ${p.case_number || ""}`.toLowerCase();
        if (!blob.includes(s)) return false;
      }
      return true;
    });
    list.sort((a, b) => {
      const pa = patientsMap[a.patient_id] || {}; const pb = patientsMap[b.patient_id] || {};
      switch (sortKey) {
        case "date_asc": return (a.created_at || "").localeCompare(b.created_at || "");
        case "pet_asc": return (pa.pet_name || "").localeCompare(pb.pet_name || "");
        case "pet_desc": return (pb.pet_name || "").localeCompare(pa.pet_name || "");
        case "doctor_asc": return (a.doctor_name || "").localeCompare(b.doctor_name || "");
        case "date_desc":
        default: return (b.created_at || "").localeCompare(a.created_at || "");
      }
    });
    return list;
  }, [items, q, from, to, doctor, sortKey, patientsMap]);

  return (
    <div className="p-4 md:p-8 max-w-[1200px] mx-auto animate-fade-in">
      <div className="mb-6">
        <h1 className="font-primary text-3xl md:text-4xl font-bold tracking-tight">History</h1>
        <p className="text-sm text-muted-foreground mt-1">Full record history — filter by date range, doctor, search, then sort.</p>
      </div>

      <Card className="p-4 mb-4 border-border/60">
        <div className="grid md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-4 space-y-1.5">
            <Label className="text-xs">Search</Label>
            <div className="relative">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input className="pl-9" placeholder="Diagnosis, pet, owner, doctor…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="report-search" />
            </div>
          </div>
          <div className="md:col-span-2 space-y-1.5">
            <Label className="text-xs">From</Label>
            <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="report-from" />
          </div>
          <div className="md:col-span-2 space-y-1.5">
            <Label className="text-xs">To</Label>
            <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="report-to" />
          </div>
          <div className="md:col-span-2 space-y-1.5">
            <Label className="text-xs">Doctor</Label>
            <Select value={doctor || "all"} onValueChange={(v) => setDoctor(v === "all" ? "" : v)}>
              <SelectTrigger data-testid="report-doctor"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All doctors</SelectItem>
                {doctors.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2 space-y-1.5">
            <Label className="text-xs">Sort</Label>
            <Select value={sortKey} onValueChange={setSortKey}>
              <SelectTrigger data-testid="report-sort"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="date_desc">Newest first</SelectItem>
                <SelectItem value="date_asc">Oldest first</SelectItem>
                <SelectItem value="pet_asc">Pet name A–Z</SelectItem>
                <SelectItem value="pet_desc">Pet name Z–A</SelectItem>
                <SelectItem value="doctor_asc">Doctor A–Z</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="p-0 border-border/60 overflow-hidden">
        {filtered.length === 0 && (
          <div className="p-12 text-center text-muted-foreground"><FileText className="h-10 w-10 mx-auto opacity-40 mb-2" />No reports match filters.</div>
        )}
        <div className="divide-y divide-border">
          {filtered.map((c) => {
            const p = patientsMap[c.patient_id] || {};
            return (
              <div key={c.id} className="px-5 py-4 flex items-center justify-between gap-3">
                <Link to={`/patients/${c.patient_id}`} className="min-w-0 flex-1">
                  <div className="font-medium">{p.pet_name || "Patient"} <span className="text-muted-foreground font-normal">— {c.primary_diagnosis || c.diagnosis_text?.slice(0, 60) || "Consultation"}</span></div>
                  <div className="text-xs text-muted-foreground">{new Date(c.created_at).toLocaleString()} · {c.doctor_name || "—"} · {p.owner_name || ""} {p.case_number && <>· {p.case_number}</>}</div>
                </Link>
                <a href={pdfUrl(c.id)} target="_blank" rel="noreferrer">
                  <Button variant="outline" size="sm"><Download className="h-4 w-4 mr-1" />PDF</Button>
                </a>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}
